import {useRef, useCallback, useState} from 'react'
import {Hero, LogoSuite, Image, Text} from '@primer/react-brand'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {IntroHero} from './IntroHero'
import {LOGOS} from './IntroLogos.data'
import {Spacer} from './_shared'
import HeroCarousel from './components/HeroCarousel/HeroCarousel'
import HeroCarouselControls, {
  type HeroCarouselControlsProps,
} from './components/HeroCarousel/HeroCarouselControls/HeroCarouselControls'
import {COPY} from './Intro.data'

export default function Intro() {
  const [currentCarouselScreenIndex, setCurrentCarouselScreenIndex] = useState<number>(0)
  const heroRef = useRef<HTMLDivElement | null>(null)
  const primaryActionRef = useRef<HTMLAnchorElement | null>(null)
  const secondaryActionRef = useRef<HTMLAnchorElement | null>(null)

  const setCopyScrollOpacity = useCallback((copyScrollOpacity: number) => {
    if (heroRef.current) {
      heroRef.current.style.opacity = `${copyScrollOpacity}` // Set the desired opacity value
    }
  }, [])

  const setCopyScrollScale = useCallback((copyScrollScale: number) => {
    if (heroRef.current) {
      heroRef.current.style.transform = `scale(${copyScrollScale})` // Set the desired scale value
    }
  }, [])

  return (
    <section id="hero">
      <div className="lp-Intro">
        {/* Header 72 + Global Banner 42 = 114px */}
        <Spacer size="114px" />
        <Spacer size="0" size768="60px" size1012="96px" />

        <IntroHero
          setCopyScrollOpacity={setCopyScrollOpacity}
          setCopyScrollScale={setCopyScrollScale}
          primaryActionRef={primaryActionRef}
          secondaryActionRef={secondaryActionRef}
        />

        {/* Hero */}
        <Hero ref={heroRef} className="lp-IntroHero sh-CtaWithTrial" data-hpc align="center">
          <Hero.Heading size="2">{COPY.hero.heading}</Hero.Heading>

          <Hero.Description size="300">{COPY.hero.description}</Hero.Description>

          <Hero.PrimaryAction
            href={COPY.hero.cta1.url}
            hasArrow={false}
            {...getAnalyticsEvent({action: 'sign_up', tag: 'button', context: 'CTAs', location: 'hero'})}
            ref={primaryActionRef}
          >
            {COPY.hero.cta1.label}
          </Hero.PrimaryAction>
          <Hero.SecondaryAction
            className="lp-IntroHero-secondaryAction"
            href={COPY.hero.cta2.url}
            hasArrow={false}
            {...getAnalyticsEvent({action: 'try_copilot', tag: 'button', context: 'CTAs', location: 'hero'})}
            ref={secondaryActionRef}
          >
            {COPY.hero.cta2.label}
            <Text className="sh-CtaWithTrial-label" size="100">
              {COPY.hero.cta2.subLabel}
            </Text>
          </Hero.SecondaryAction>
        </Hero>

        <Spacer className="lp-IntroVisuals-spacer" size="64px" size768="560px" size1012="660px" />

        {/* Carousel */}
        <HeroCarousel
          isVisible
          currentScreenIndex={currentCarouselScreenIndex}
          onAutoPlayTimeout={setCurrentCarouselScreenIndex}
          playButtonAriaLabel={COPY.carousel.aria.playButton}
        />
      </div>

      {/* Carousel controls */}
      <HeroCarouselControls
        items={COPY.carousel.items as HeroCarouselControlsProps['items']}
        currentIndex={currentCarouselScreenIndex}
        onChange={setCurrentCarouselScreenIndex}
        toggleAriaLabel={COPY.carousel.aria.controlsToggle}
      />

      {/* Logos */}
      <LogoSuite className="IntroLogoSuite">
        <LogoSuite.Heading visuallyHidden>{COPY.logoSuite.heading}</LogoSuite.Heading>
        <LogoSuite.Logobar marquee marqueeSpeed="slow">
          {LOGOS.map((logo, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Image key={index} src={logo.image} alt={logo.alt} style={{height: logo.height}} loading="lazy" />
          ))}
        </LogoSuite.Logobar>
      </LogoSuite>
    </section>
  )
}

try{ Intro.displayName ||= 'Intro' } catch {}